/**
 * AddContext (ported from Noteble's capture flow — ADR-022).
 *
 * Owns the multi-element capture state for /add: photos and notes, with
 * IndexedDB persistence (standalone slot) so captured elements survive a
 * page refresh. Voice transcripts live in the workflow FSM (they arrive
 * already transcribed via the browser-direct Transcribe/Web Speech engines).
 *
 * SystemDox divergence from Noteble: photos upload to the tenant media store
 * at capture time (presigned PUT) and get a best-effort Claude-vision
 * description — so the assembly job can reference them as media:// ids and
 * send them to the model as vision blocks.
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import {
  indexedDBSessionService,
  STANDALONE_SESSION_ID,
  type InterruptedRecording,
  type CapturedPhoto,
  type RecordingNote,
} from '@/services/indexedDBSessionService';
import { mediaApi, uploadToPresignedUrl } from '@/services/mediaApi';
import { aiApi } from '@/services/aiApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('AddContext');

export type { CapturedPhoto, RecordingNote };

export interface UploadStatusEntry {
  progress: number;
  status: 'uploading' | 'uploaded' | 'failed';
}

interface AddContextValue {
  // --- Photos and Notes ---
  photos: CapturedPhoto[];
  notes: RecordingNote[];
  uploadStatuses: Map<string, UploadStatusEntry>;
  addPhoto: (blob: Blob) => void;
  removePhoto: (photoId: string) => void;
  updatePhotoDescription: (photoId: string, description: string) => void;
  retryPhotoUpload: (photoId: string) => void;
  addNote: (text: string, sourceType?: 'note' | 'document', sourceTitle?: string) => void;
  updateNote: (noteId: string, text: string) => void;
  removeNote: (noteId: string) => void;

  // --- Validation ---
  validateBeforeSubmit: () => string | null;

  // --- Draft restore / clearing ---
  restorePhotos: (photos: CapturedPhoto[]) => void;
  restoreNotes: (notes: RecordingNote[]) => void;

  /** Await in-flight uploads (retrying failures once); resolves with the
   *  latest photos — those still missing a mediaId could not be uploaded. */
  ensurePhotosUploaded: () => Promise<CapturedPhoto[]>;
}

const AddContext = createContext<AddContextValue | undefined>(undefined);

export const AddProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const photosRef = useRef<CapturedPhoto[]>([]);
  const notesRef = useRef<RecordingNote[]>([]);
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [notes, setNotes] = useState<RecordingNote[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<Map<string, UploadStatusEntry>>(new Map());
  const uploadsInFlightRef = useRef<Map<string, Promise<void>>>(new Map());

  /**
   * Persist photos + notes under the standalone slot so they survive a page
   * refresh (Noteble persisted photos only; notes ride along here too).
   */
  const saveStandalone = useCallback(async () => {
    if (photosRef.current.length === 0 && notesRef.current.length === 0) {
      indexedDBSessionService.deleteRecording(STANDALONE_SESSION_ID).catch(() => {});
      return;
    }
    const entry: InterruptedRecording = {
      sessionId: STANDALONE_SESSION_ID,
      chunks: [],
      mimeType: '',
      duration: 0,
      languageCode: '',
      startedAt: Date.now(),
      lastSavedAt: Date.now(),
      photos: photosRef.current.length > 0 ? [...photosRef.current] : undefined,
      notes: notesRef.current.length > 0 ? [...notesRef.current] : undefined,
    };
    await indexedDBSessionService.saveRecording(entry).catch((err) =>
      log.warn('Failed to save standalone capture to IndexedDB', { error: String(err) }),
    );
  }, []);

  const setStatus = useCallback((photoId: string, entry: UploadStatusEntry | null) => {
    setUploadStatuses((prev) => {
      const next = new Map(prev);
      if (entry) next.set(photoId, entry);
      else next.delete(photoId);
      return next;
    });
  }, []);

  const patchPhoto = useCallback((photoId: string, patch: Partial<CapturedPhoto>) => {
    const photo = photosRef.current.find((p) => p.id === photoId);
    if (!photo) return;
    Object.assign(photo, patch);
    setPhotos([...photosRef.current]);
  }, []);

  /**
   * Upload a photo to the tenant media store, then run best-effort vision
   * analysis to pre-fill its description and mermaid rendition. An analysis
   * failure keeps the photo attached (spec: vision failure loses nothing).
   */
  const uploadAndAnalyze = useCallback(async (photoId: string) => {
    const photo = photosRef.current.find((p) => p.id === photoId);
    if (!photo || photo.mediaId) return;

    setStatus(photoId, { progress: 0, status: 'uploading' });
    try {
      const contentType = photo.blob.type || 'image/jpeg';
      const target = await mediaApi.getUploadUrl(contentType);
      await uploadToPresignedUrl(target.uploadUrl, photo.blob, contentType, (percent) =>
        setStatus(photoId, { progress: percent, status: 'uploading' }),
      );
      patchPhoto(photoId, { mediaId: target.mediaId });
      setStatus(photoId, { progress: 100, status: 'uploaded' });
      void saveStandalone();
    } catch (err) {
      log.warn('Photo upload failed', { photoId, error: String(err) });
      setStatus(photoId, { progress: 0, status: 'failed' });
      return;
    }

    try {
      const current = photosRef.current.find((p) => p.id === photoId);
      if (!current?.mediaId) return;
      const analysis = await aiApi.analyzeImage(current.mediaId);
      const extracted = [analysis.description, ...analysis.requirements].filter(Boolean).join('\n');
      patchPhoto(photoId, {
        mermaid: analysis.mermaid,
        // Never clobber a description the user already typed.
        ...(current.description ? {} : { description: extracted }),
      });
      void saveStandalone();
    } catch (err) {
      log.warn('Photo analysis failed (photo stays attached)', { photoId, error: String(err) });
    }
  }, [patchPhoto, saveStandalone, setStatus]);

  const startUpload = useCallback((photoId: string) => {
    const task = uploadAndAnalyze(photoId).finally(() => {
      uploadsInFlightRef.current.delete(photoId);
    });
    uploadsInFlightRef.current.set(photoId, task);
  }, [uploadAndAnalyze]);

  // On mount: restore standalone photos/notes from IndexedDB (survives refresh).
  useEffect(() => {
    indexedDBSessionService.getRecording(STANDALONE_SESSION_ID).then((entry) => {
      if (!entry) return;
      if (photosRef.current.length === 0 && entry.photos?.length) {
        photosRef.current = [...entry.photos];
        setPhotos(photosRef.current);
        // Resume uploads that never completed; uploaded ones show as done.
        for (const p of entry.photos) {
          if (p.mediaId) setStatus(p.id, { progress: 100, status: 'uploaded' });
          else startUpload(p.id);
        }
        log.info('Restored standalone photos from IndexedDB', { count: entry.photos.length });
      }
      if (notesRef.current.length === 0 && entry.notes?.length) {
        notesRef.current = [...entry.notes];
        setNotes(notesRef.current);
        log.info('Restored standalone notes from IndexedDB', { count: entry.notes.length });
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPhoto = useCallback((blob: Blob) => {
    const photo: CapturedPhoto = {
      id: crypto.randomUUID(),
      blob,
      timestamp: 0,
      capturedAt: Date.now(),
    };
    photosRef.current.push(photo);
    setPhotos([...photosRef.current]);
    void saveStandalone();
    startUpload(photo.id);
    log.debug('Photo added', { photoId: photo.id });
  }, [saveStandalone, startUpload]);

  const removePhoto = useCallback((photoId: string) => {
    photosRef.current = photosRef.current.filter((p) => p.id !== photoId);
    setPhotos([...photosRef.current]);
    setStatus(photoId, null);
    void saveStandalone();
  }, [saveStandalone, setStatus]);

  const updatePhotoDescription = useCallback((photoId: string, description: string) => {
    patchPhoto(photoId, { description });
    void saveStandalone();
  }, [patchPhoto, saveStandalone]);

  const retryPhotoUpload = useCallback((photoId: string) => {
    if (uploadsInFlightRef.current.has(photoId)) return;
    startUpload(photoId);
  }, [startUpload]);

  const addNote = useCallback((text: string, sourceType?: 'note' | 'document', sourceTitle?: string) => {
    const note: RecordingNote = {
      id: crypto.randomUUID(),
      text,
      timestamp: 0,
      createdAt: Date.now(),
      sourceType,
      sourceTitle,
    };
    notesRef.current.push(note);
    setNotes([...notesRef.current]);
    void saveStandalone();
    log.debug('Note added', { noteId: note.id });
  }, [saveStandalone]);

  const updateNote = useCallback((noteId: string, text: string) => {
    const note = notesRef.current.find((n) => n.id === noteId);
    if (note) {
      note.text = text;
      setNotes([...notesRef.current]);
      void saveStandalone();
    }
  }, [saveStandalone]);

  const removeNote = useCallback((noteId: string) => {
    notesRef.current = notesRef.current.filter((n) => n.id !== noteId);
    setNotes([...notesRef.current]);
    void saveStandalone();
  }, [saveStandalone]);

  const validateBeforeSubmit = useCallback((): string | null => {
    if (!navigator.onLine) {
      return 'No internet connection';
    }
    return null;
  }, []);

  const restorePhotos = useCallback((restoredPhotos: CapturedPhoto[]) => {
    photosRef.current = [...restoredPhotos];
    setPhotos([...restoredPhotos]);
    setUploadStatuses(new Map());
    for (const p of restoredPhotos) {
      if (p.mediaId) setStatus(p.id, { progress: 100, status: 'uploaded' });
      else startUpload(p.id);
    }
    void saveStandalone();
    log.info('Photos restored', { count: restoredPhotos.length });
  }, [saveStandalone, setStatus, startUpload]);

  const restoreNotes = useCallback((restoredNotes: RecordingNote[]) => {
    notesRef.current = [...restoredNotes];
    setNotes([...restoredNotes]);
    void saveStandalone();
    log.info('Notes restored', { count: restoredNotes.length });
  }, [saveStandalone]);

  const ensurePhotosUploaded = useCallback(async (): Promise<CapturedPhoto[]> => {
    // Wait for anything already in flight.
    await Promise.all([...uploadsInFlightRef.current.values()]);
    // One retry pass for photos that still have no mediaId.
    const missing = photosRef.current.filter((p) => !p.mediaId);
    if (missing.length > 0) {
      missing.forEach((p) => startUpload(p.id));
      await Promise.all([...uploadsInFlightRef.current.values()]);
    }
    return [...photosRef.current];
  }, [startUpload]);

  const value: AddContextValue = {
    photos,
    notes,
    uploadStatuses,
    addPhoto,
    removePhoto,
    updatePhotoDescription,
    retryPhotoUpload,
    addNote,
    updateNote,
    removeNote,
    validateBeforeSubmit,
    restorePhotos,
    restoreNotes,
    ensurePhotosUploaded,
  };

  return <AddContext.Provider value={value}>{children}</AddContext.Provider>;
};

export function useAdd(): AddContextValue {
  const context = useContext(AddContext);
  if (!context) {
    throw new Error('useAdd must be used within an AddProvider');
  }
  return context;
}
